import { formatEatDateTime, type Meal } from '@wodi/domain';
import type { KitchenAllergiesView, MealListRow } from '@wodi/shared';
import { Tag } from '@wodi/ui';

import styles from '../operations/operations.module.css';

// Words shared by the kitchen screens (docs/plans/phase3-kitchen-and-housekeeping.md §1.4).
// Allergies are always explicit (CLAUDE.md): a list, "No known allergies" or "Allergies not
// recorded", never blank.

export const RESTRICTED = 'Restricted record';

export const MEAL_OPTIONS: readonly { id: Meal; label: string }[] = [
  { id: 'breakfast', label: 'Breakfast' },
  { id: 'lunch', label: 'Lunch' },
  { id: 'supper', label: 'Supper' },
];

/** The meal to show first: the next one today, else supper. */
export function nextMeal(now: Date): Meal {
  const minutes = ((now.getUTCHours() + 3) % 24) * 60 + now.getUTCMinutes();
  if (minutes < 7 * 60 + 30) return 'breakfast';
  if (minutes < 13 * 60) return 'lunch';
  return 'supper';
}

export function allergyWords(allergies: KitchenAllergiesView): string {
  switch (allergies.state) {
    case 'list':
      return allergies.allergies
        .map((a) => (a.category === null ? a.substance : `${a.substance} (${a.category})`))
        .join(', ');
    case 'no_known_allergies':
      return 'No known allergies';
    case 'not_recorded':
      return 'Allergies not recorded';
  }
}

/** Allergies on screen: a documented allergy is clinical danger (DESIGN.md). */
export function Allergies({ allergies }: { allergies: KitchenAllergiesView }) {
  if (allergies.state !== 'list') return <>{allergyWords(allergies)}</>;
  return (
    <span className={styles.tags}>
      {allergies.allergies.map((a) => (
        <Tag key={a.substance} state="danger">
          {a.category === null ? a.substance : `${a.substance} (${a.category})`}
        </Tag>
      ))}
    </span>
  );
}

export function patientWords(row: MealListRow): string {
  return row.patient === null ? RESTRICTED : row.patient.displayName;
}

export function bedWords(row: MealListRow): string {
  return row.bedLabel === null ? 'No bed yet' : `Bed ${row.bedLabel}`;
}

export function dietWords(row: MealListRow): string {
  if (row.diet === null) return 'No diet ordered: ask the ward';
  const parts = [row.diet.diet];
  if (row.diet.texture !== null) parts.push(`${row.diet.texture.toLowerCase()} texture`);
  return parts.join(', ');
}

export function fluidWords(ml: number | null): string {
  return ml === null ? 'No restriction' : `${ml.toLocaleString('en-KE')} mL a day`;
}

export function nbmUntilWords(until: string | null): string {
  return until === null
    ? 'Until the diet is changed'
    : `Until ${formatEatDateTime(new Date(until))}`;
}
